// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

// Code generated by Microsoft (R) AutoRest Code Generator.
// Changes may cause incorrect behavior and will be lost if the code is regenerated.

import type { PagedAsyncIterableIterator, PageSettings } from "@azure/core-paging";
import { setContinuationToken } from "../pagingHelper.js";
import type { WorkflowVersions } from "../operationsInterfaces/index.js";
import * as coreClient from "@azure/core-client";
import * as Mappers from "../models/mappers.js";
import * as Parameters from "../models/parameters.js";
import type { WebSiteManagementClient } from "../webSiteManagementClient.js";
import type {
  WorkflowVersion,
  WorkflowVersionsListNextOptionalParams,
  WorkflowVersionsListOptionalParams,
  WorkflowVersionsListResponse,
  WorkflowVersionsGetOptionalParams,
  WorkflowVersionsGetResponse,
  WorkflowVersionsListNextResponse,
} from "../models/index.js";

/// <reference lib="esnext.asynciterable" />
/** Class containing WorkflowVersions operations. */
export class WorkflowVersionsImpl implements WorkflowVersions {
  private readonly client: WebSiteManagementClient;

  /**
   * Initialize a new instance of the class WorkflowVersions class.
   * @param client Reference to the service client
   */
  constructor(client: WebSiteManagementClient) {
    this.client = client;
  }

  /**
   * Gets a list of workflow versions.
   * @param resourceGroupName Name of the resource group to which the resource belongs.
   * @param name Site name.
   * @param workflowName The workflow name.
   * @param options The options parameters.
   */
  public list(
    resourceGroupName: string,
    name: string,
    workflowName: string,
    options?: WorkflowVersionsListOptionalParams,
  ): PagedAsyncIterableIterator<WorkflowVersion> {
    const iter = this.listPagingAll(resourceGroupName, name, workflowName, options);
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listPagingPage(resourceGroupName, name, workflowName, options, settings);
      },
    };
  }

  private async *listPagingPage(
    resourceGroupName: string,
    name: string,
    workflowName: string,
    options?: WorkflowVersionsListOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<WorkflowVersion[]> {
    let result: WorkflowVersionsListResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._list(resourceGroupName, name, workflowName, options);
      const page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listNext(
        resourceGroupName,
        name,
        workflowName,
        continuationToken,
        options,
      );
      continuationToken = result.nextLink;
      const page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listPagingAll(
    resourceGroupName: string,
    name: string,
    workflowName: string,
    options?: WorkflowVersionsListOptionalParams,
  ): AsyncIterableIterator<WorkflowVersion> {
    for await (const page of this.listPagingPage(resourceGroupName, name, workflowName, options)) {
      yield* page;
    }
  }

  /**
   * Gets a list of workflow versions.
   * @param resourceGroupName Name of the resource group to which the resource belongs.
   * @param name Site name.
   * @param workflowName The workflow name.
   * @param options The options parameters.
   */
  private _list(
    resourceGroupName: string,
    name: string,
    workflowName: string,
    options?: WorkflowVersionsListOptionalParams,
  ): Promise<WorkflowVersionsListResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, name, workflowName, options },
      listOperationSpec,
    );
  }

  /**
   * Gets a workflow version.
   * @param resourceGroupName Name of the resource group to which the resource belongs.
   * @param name Site name.
   * @param workflowName The workflow name.
   * @param versionId The workflow versionId.
   * @param options The options parameters.
   */
  get(
    resourceGroupName: string,
    name: string,
    workflowName: string,
    versionId: string,
    options?: WorkflowVersionsGetOptionalParams,
  ): Promise<WorkflowVersionsGetResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, name, workflowName, versionId, options },
      getOperationSpec,
    );
  }

  /**
   * ListNext
   * @param resourceGroupName Name of the resource group to which the resource belongs.
   * @param name Site name.
   * @param workflowName The workflow name.
   * @param nextLink The nextLink from the previous successful call to the List method.
   * @param options The options parameters.
   */
  private _listNext(
    resourceGroupName: string,
    name: string,
    workflowName: string,
    nextLink: string,
    options?: WorkflowVersionsListNextOptionalParams,
  ): Promise<WorkflowVersionsListNextResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, name, workflowName, nextLink, options },
      listNextOperationSpec,
    );
  }
}
// Operation Specifications
const serializer = coreClient.createSerializer(Mappers, /* isXml */ false);

const listOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Web/sites/{name}/hostruntime/runtime/webhooks/workflow/api/management/workflows/{workflowName}/versions",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.WorkflowVersionListResult,
    },
    default: {
      bodyMapper: Mappers.ErrorResponse,
    },
  },
  queryParameters: [Parameters.apiVersion, Parameters.top1],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.name,
    Parameters.workflowName1,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const getOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Web/sites/{name}/hostruntime/runtime/webhooks/workflow/api/management/workflows/{workflowName}/versions/{versionId}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.WorkflowVersion,
    },
    default: {
      bodyMapper: Mappers.ErrorResponse,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.name,
    Parameters.workflowName1,
    Parameters.versionId,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.WorkflowVersionListResult,
    },
    default: {
      bodyMapper: Mappers.ErrorResponse,
    },
  },
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.name,
    Parameters.nextLink,
    Parameters.workflowName1,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
